import React, { useMemo, useState } from 'react'
import styled from 'styled-components'

import { icons } from 'utils/icons'
import { MODALS, TITLES } from 'locales/locales'
import { IChannel, IGroup } from 'types/group.types'

import Channel from './Channel'
import CustomText from 'components/text/CustomText'
import CustomIcon from 'components/icon/CustomIcon'
import OptionListCard from 'components/cards/OptionListCard'
import Avatar, { AvatarSize } from 'components/avatar/Avatar'
import colors from 'utils/colors'
import { useTranslation } from 'react-i18next'
import Tooltip from 'components/kendo/tooltips/Tooltip'
import { useAppSelector } from 'app/hooks'

export const GROUP_OPTION_LIST_DATA_FOR_WITHOUT_MY_GROUPS = [
  { key: 'group_info', value: TITLES.TEAM_INFO_TITLE },
]

export const GROUP_OPTON_LIST_FOR_MY_GROUPS = [
  { key: 'group_info', value: TITLES.TEAM_INFO_TITLE },
  { key: 'create_new_channel', value: MODALS.CREATE_NEW_CHANNEL_TITLE },
]

interface IGroupProps {
  isMyGroup: boolean
  groupId: string
  url: string
  name: string
  channels: Array<IChannel>
  pathname: string
  isSearch: boolean
  group: IGroup
  onSelectRoom: (channel: IChannel, group: IGroup, isAppear?: boolean) => void
  onCreateChannel: (groupId: string) => void
  onOpenGroupInfo: (groupId: string) => void
}

const Group = ({
  isMyGroup,
  groupId,
  url,
  name,
  channels,
  pathname,
  isSearch,
  group,
  onSelectRoom,
  onCreateChannel,
  onOpenGroupInfo,
}: IGroupProps) => {
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState<number | undefined>()
  const [isExpanded, setIsExpanded] = useState(true)

  const { t } = useTranslation()


  const isRTL = useAppSelector((state) => state.auth.user.notificationLanguage) === 'ar';

  const onClickGroupOption = (e: React.MouseEvent<HTMLElement>) => {
    setVisible(true)
    setPosition(e.nativeEvent.pageY)
    e.stopPropagation()
  }

  const onSelectOption = (key: string) => {
    if (key === 'create_new_channel') {
      onCreateChannel(groupId)
      onHideOption()
    } else if (key === 'group_info') {
      onOpenGroupInfo(groupId)
      onHideOption()
    }
  }

  const onHideOption = () => {
    setVisible(false)
    setPosition(undefined)
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const OPTION_LIST = useMemo(
    () =>
      isMyGroup
        ? GROUP_OPTON_LIST_FOR_MY_GROUPS.map((grp: any)=>{
          return {
            ...grp,
            title: t(grp?.title),
            value: t(grp?.value),
          }
        })
        : GROUP_OPTION_LIST_DATA_FOR_WITHOUT_MY_GROUPS.map((grp: any) => {
            return {
              ...grp,
              value: t(grp?.value), 
            }
          }),
    [isMyGroup, isRTL, t],
  )

  const NotificationView = useMemo(() => {
    const totalNotificationCount = (channels || []).reduce(
      (acc, channel) => acc + (Number(channel?.notificationCount) || 0),
      0
    )
    return (
      !!totalNotificationCount && (
        <CountWrapper>
          <StyledCountText color={colors.darkBlue} typography="body7">
            {totalNotificationCount}
          </StyledCountText>
        </CountWrapper>
      )
    )
  }, [channels])

  const getTeamName = (name: string) => {
      if (!name) return '';
    return name.length > 15 ? `${name.substring(0, 15)}...` : name
  }

  return (
    <Wrapper>
      {isMyGroup && (
        <GroupOptionListCard
          visible={visible}
          position={position}
          items={OPTION_LIST}
          onClick={ onSelectOption}
          onHideCard={onHideOption}
          isRTL={isRTL}
        />
      )}
      <Row onClick={toggleExpanded}>
        <DescriptionWrapper>
          <ChevronIcon 
            iconPreset="small" 
            source={isExpanded ? icons.chevronUpWhiteIcon : icons.chevronDownWhiteIcon}
            onClick={toggleExpanded}
          />
          {group.isArchived ? (
            <CustomIcon iconPreset="small" source={icons.unarchiveIcon} />
          ) : (
            <Avatar
              text={name}
              source={url}
              size={AvatarSize.small}
              color={group?.backgroundColor || colors.orange}
            />
          )}
          <Tooltip
            content={
              <OverflowText
                typography="body9"
                color={colors.white}
                title={name?.length > 15 ? name : undefined}
              >
               {getTeamName(name || '')}
              </OverflowText>
            }
            anchorElement="target"
            openDelay={250}
            parentTitle={true}
            position="right"
          />
        </DescriptionWrapper>
        {NotificationView}
        {isMyGroup && !group.isArchived && (
          <ThreeDotIcon source={icons.threeDotIconWhite} onClick={onClickGroupOption} />
        )}
      </Row>
      {isExpanded && channels?.map((channel) => (
        <Channel
          channel={channel}
          group={group}
          key={channel._id}
          isSearch={isSearch}
          selectedRoom={pathname.includes(channel._id)}
          onSelectRoom={onSelectRoom}
        />
      ))}
    </Wrapper>
  )
}

export default Group

const OverflowText = styled(CustomText)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Wrapper = styled.div`
  padding: 10px 4px 10px 16px;
`

const ThreeDotIcon = styled(CustomIcon)`
  cursor: pointer;
  &:hover {
    opacity: 0.8;
    transform: scale(1.1);
    background-color: ${colors.vime50};
    border-radius: 50%;
  }
`

const CountWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 18px;
  padding: 0 6px;
  height: 18px;
  border-radius: 50px;
  background-color: ${colors.white};
  color: ${colors.darkBlue};
`

const StyledCountText = styled(CustomText)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap; 
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px;
  cursor: pointer;
`

const DescriptionWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ChevronIcon = styled(CustomIcon)`
  cursor: pointer;
  transition: transform 0.1s ease;
`


export const GroupOptionListCard = styled(OptionListCard).attrs(
  (props: { position: number; isRTL?: boolean }) => props,
)<{ position: number; isRTL?: boolean }>`
  top: ${({ position }) => position + 10}px;
  ${({ isRTL }) =>
    isRTL ? `
      right: 210px;
    ` : `
      left: 210px;
    `}

`;
